import React from "react"

import { FormattedMessage, useIntl } from "react-intl";

import { darken, styled, alpha } from "@mui/material/styles";

import { useTheme } from "@mui/styles";

import {
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from "@mui/material"

import CustomIcon from "../custom-icon";

import Search from '@mui/icons-material/Search';
import Clear from '@mui/icons-material/Clear';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CommonSearchPaginateSkeleton from "../client-portal/common-search-paginate-skeleton";


const FilterStyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== "defaultcolor",
})(({ defaultcolor }) => ({
  backgroundColor: "#fff !important",
  borderRadius: "12px",

  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    height: "48px !important",

    // Hover — seletor mais específico
    "&:not(.Mui-disabled):not(.Mui-error):hover .MuiOutlinedInput-notchedOutline": {
      borderColor: `${defaultcolor} !important`,
      borderWidth: "1px",
    },

    // Focused — seletor mais específico
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: `${defaultcolor} !important`,
      borderWidth: "2px",
    },

    // Disabled
    "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
      borderColor: `${defaultcolor} !important`,
      borderWidth: "1px",
      opacity: 0.5,
    },

    // Error
    "&.Mui-error .MuiOutlinedInput-notchedOutline": {
      borderColor: `${defaultcolor} !important`,
      borderWidth: "1px",
    },
  },
  "& .MuiInputBase-input": {
    padding: "12px 16px",
    fontSize: "0.95rem",
  },
}));

const StyledFormControl = styled(FormControl, {
  shouldForwardProp: (prop) =>
    ![
      "iconColor",
      "customBorderColor"
    ].includes(prop),
})(
  ({
    iconColor,
    customBorderColor
  }) => ({
    "& .MuiOutlinedInput-root": {
      height: "48px !important",
      backgroundColor: "#fff !important",
      borderRadius: "8px",
      "&.Mui-focused fieldset": {
        border: `2px solid ${customBorderColor}`,
      },
    },

    "& .MuiSvgIcon-root": {
      fill: iconColor,
    },
  })
);

const StyledMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) =>
    !["customHoverBgColor", "customHoverTextColor", "customSelectedBgColor", "customSelectedTextColor"].includes(prop),
})(
  ({ customHoverBgColor, customHoverTextColor, customSelectedBgColor, customSelectedTextColor }) => ({
    "&:hover": {
      backgroundColor: customHoverBgColor,
      color: customHoverTextColor,
    },
    "&.Mui-selected": {
      backgroundColor: customSelectedBgColor,
      color: customSelectedTextColor,
    },
    "&.Mui-selected:hover": {
      backgroundColor: customHoverBgColor,
      color: customHoverTextColor,
    },
  })
);

const CustomButton = styled(Button)(() => ({
  color: "#fff !important",
  borderRadius: "8px",
  padding: "12px 24px",

  "&:disabled": {
    backgroundColor: "#d1d5db !important",
    color: "initial !important",
  },
}));

const ClearButton = styled(Button)(({ getCustomColorPrimary }) => ({
  //variant outlined, então cor é controlada por border e text, não por background
  color: getCustomColorPrimary(0.5),
  borderRadius: "8px",
  padding: "12px 24px",
  borderColor: getCustomColorPrimary(),

  "&:hover": {
    borderColor: getCustomColorPrimary(0.1),
    backgroundColor: `${alpha(getCustomColorPrimary(0, true), 0.1)} !important`,
  },

  "&:disabled": {
    backgroundColor: "#d1d5db !important",
    borderColor: "#d1d5db !important",
    color: "initial !important",
  },
}));

const FilterStyledChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "customcolor",
})(({ customcolor }) => ({
  backgroundColor: `${alpha(customcolor, 0.1)} !important`, // 20 = 12% opacity no hex
  borderColor: customcolor,
  color: `${customcolor} !important`,
  fontWeight: 500,

  // Ícone de delete (x)
  "& .MuiChip-deleteIcon": {
    color: `${customcolor} !important`,
    "&:hover": {
      color: `${customcolor} !important`,
      opacity: 0.7,
    },
  },

  "&:hover": {
    backgroundColor: `${alpha(customcolor, 0.3)} !important`,
  },
}));

const CustomSelect = ({
  value,
  handleChange,
  label,
  id,
  menuItemsList = [],
  getCustomColorPrimary
}) => {
  const accentColorWithImportant = getCustomColorPrimary();
  const accentColorWithoutImportant = getCustomColorPrimary(0, true);

  const accentBackgroundColor = alpha(accentColorWithoutImportant, 0.2);

  return (
    <StyledFormControl
      fullWidth
      size="small"
      iconColor={accentColorWithoutImportant}
      customBorderColor={accentColorWithImportant}
    >
      <InputLabel id={`${id}-label`}>{label}</InputLabel>
      <Select
        labelId={`${id}-label`}
        id={id}
        value={value}
        onChange={handleChange}
        label={label}
      >
        <StyledMenuItem
          key={0}
          value={0}
          customSelectedTextColor={accentColorWithImportant}
          customSelectedBgColor={`${accentBackgroundColor} !important`}

          customHoverTextColor={accentColorWithImportant}
          customHoverBgColor={`${accentBackgroundColor} !important`}
        >
          <em><FormattedMessage id="label.search" /></em>
        </StyledMenuItem>

        {menuItemsList.map((item) => {
          return (
            <StyledMenuItem
              key={item.value}
              value={item.value}
              customSelectedTextColor={accentColorWithImportant}
              customSelectedBgColor={`${accentBackgroundColor} !important`}

              customHoverTextColor={accentColorWithImportant}
              customHoverBgColor={`${accentBackgroundColor} !important`}
            >
              {item.label}
            </StyledMenuItem>
          );
        })}
      </Select>
    </StyledFormControl>
  )
}

export default function KnowledgeBaseExternalHeaderSearchList({
  clientPortal,
  state,
  loading,
  loadingKnowledge,

  clearFields,
  callSearch,
  addFilter,

  handleDelete,
  changeValues
}) {
  const intl = useIntl();
  const theme = useTheme();

  const primaryColor = theme.palette.primary.main;
  const _defaultClientColor = clientPortal?.defaultcolor ? clientPortal?.defaultcolor : null;
  const defaultClientColor = _defaultClientColor || primaryColor;

  const contentFilterArray = (state?.contentFilter || "").split("\n").filter(i => i.trim() != "");

  const arrayToDisabledClearFilter = [loading, loadingKnowledge, !!state.searchValue, !!contentFilterArray.length];
  const disabledClearFilter = arrayToDisabledClearFilter.some(item => item === true);

  function formattedListToCustomSelectOptions(list) {
    return list.map(item => ({
      value: item.id,
      label: item.name
    }))
  }

  function getCustomColorPrimary(darkenValue = 0, customIcon = false) {
    const customColor = darken(defaultClientColor, darkenValue);

    if (customIcon) return customColor;

    return `${customColor} !important`;
  }

  function getCommonButtonStyleWithHover(options = {}) {
    const {
      mainBackgroundColor = getCustomColorPrimary(0.1),
      hoverBackgroundColor = getCustomColorPrimary(0.2),
      mainCss = {},
      hoverCss = {}
    } = options;

    return {
      backgroundColor: mainBackgroundColor,
      ...mainCss,
      ":hover": {
        backgroundColor: hoverBackgroundColor,
        ...hoverCss
      }
    }
  }

  if (loading) {
    return (
      <Grid item xs={12} sm={12} sx={{ marginInline: "15vw" }}>
        <CommonSearchPaginateSkeleton loading={true} />
      </Grid>
    )
  }

  return (
    <React.Fragment>
      {state.contentFilter &&
        <Grid item xs={12} sm={12} sx={{ marginInline: "15vw" }}>
          <Card>
            <CardContent>
              <Grid container spacing={1} alignItems="flex-end" >
                <Grid item xs={8} md={6}>
                  <Typography variant="h5">
                    {intl.formatMessage({ id: "filters" })}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <strong style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                    {state.contentFilter.split("\n").map((i, key) => {
                      if (i.trim() != "") {
                        return <FilterStyledChip
                          key={key}
                          label={i}
                          onDelete={() => handleDelete(key)}
                          color="primary"
                          variant="outlined"
                          customcolor={getCustomColorPrimary(0, true)}
                        />
                      }
                    })}
                  </strong>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      }

      <Grid item xs={12} sm={12} sx={{ marginInline: "15vw" }}>
        <Card className="background-title-search">
          <CardContent>
            <Grid container spacing={1.5} sx={12} alignItems="center">
              {/* Campo de busca */}
              <Grid item xs={12} md={4.5}>
                <CustomSelect
                  id="field-select"
                  label={intl.formatMessage({ id: "field" })}
                  name="filterSelected"
                  getCustomColorPrimary={getCustomColorPrimary}
                  menuItemsList={formattedListToCustomSelectOptions(state.filters)}
                  handleChange={(e) => changeValues(e, "filterSelected")}
                  value={state.filterSelected}
                />
              </Grid>

              {/* Filtro por assunto */}
              <Grid item xs={12} md={4.5}>
                <FilterStyledTextField
                  name="searchValue"
                  placeholder={intl.formatMessage({ id: "search" })}
                  value={state.searchValue}
                  onChange={changeValues}
                  variant="outlined"
                  size="small"
                  defaultcolor={getCustomColorPrimary(0, true)}
                  fullWidth
                  required
                />
              </Grid>

              {/* { Botão de adicionar filtro } */}
              <Grid item xs={12} md={1}>
                <CustomButton
                  fullWidth
                  onClick={addFilter}
                  sx={getCommonButtonStyleWithHover()}
                  disabled={loading || loadingKnowledge || !state.searchValue}
                >
                  <CustomIcon icon={ZoomInIcon} color="#fff" />
                </CustomButton>
              </Grid>

              {/* Botão de busca */}
              <Grid item xs={12} md={1}>
                <CustomButton
                  fullWidth
                  onClick={() => callSearch()}
                  sx={getCommonButtonStyleWithHover()}
                  disabled={loading || loadingKnowledge}
                >
                  <CustomIcon icon={Search} color="#fff" />
                </CustomButton>
              </Grid>

              {/* { Botão de limpar } */}
              <Grid item xs={12} md={1}>
                <ClearButton
                  variant="outlined"
                  fullWidth
                  onClick={clearFields}
                  getCustomColorPrimary={getCustomColorPrimary}
                  disabled={!disabledClearFilter}
                >
                  <CustomIcon icon={Clear} color={getCustomColorPrimary(0.5, true)} />
                </ClearButton>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </React.Fragment>
  )
}